import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import {
  EditRecord,
  initialState,
} from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { Validation } from '@efdigital/ll-admin-v2/components/validation.gts';
import type { UserInfo } from '@efdigital/ll-admin-v2/data/builders/system/users';
import { queryCoachProfilesByUserIds } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import {
  type AugmentedSuggestion,
  BaseUserPicker,
} from '../pickers/user-picker.gts';

export class CoachUserPicker extends BaseUserPicker<CoachProfile | undefined> {
  @service declare store: Store;

  knownProfiles: Record<string, { profile: CoachProfile | null }> = {};

  private async lookupProfiles(suggestions: UserInfo[]) {
    const unknownProfiles: string[] = [];
    for (const suggestion of suggestions) {
      if (!this.knownProfiles[suggestion.id]) {
        unknownProfiles.push(suggestion.id);
      }
    }

    if (unknownProfiles.length > 0) {
      const profiles = await this.store.request(
        queryCoachProfilesByUserIds(unknownProfiles)
      );
      for (const profile of profiles.content.data) {
        const user = profile.user;
        const index = unknownProfiles.indexOf(user);
        if (index !== -1) {
          unknownProfiles.splice(index, 1);
          this.knownProfiles[profile.user] = { profile };
        }
      }

      for (const profile of unknownProfiles) {
        this.knownProfiles[profile] = { profile: null };
      }
    }
  }

  async augmentSuggestions(
    suggestions: UserInfo[]
  ): Promise<AugmentedSuggestion<CoachProfile | undefined>[]> {
    // Trim suggestions to top 50
    suggestions = suggestions.slice(0, 50);

    await this.lookupProfiles(suggestions);

    return suggestions.map((suggestion) => {
      const profile = this.knownProfiles[suggestion.id]?.profile ?? undefined;
      return {
        user: suggestion,
        meta: profile,
        disabled: profile ? true : false,
      };
    });
  }

  filterSuggestions(
    suggestions: AugmentedSuggestion<CoachProfile | undefined>[]
  ): AugmentedSuggestion<CoachProfile | undefined>[] {
    return suggestions;
  }
}

export class CreateCoach extends Component<EditRecordSignature<CoachProfile>> {
  setString =
    (field: 'prefix' | 'firstName' | 'lastName' | 'suffix') =>
    (event: Event) => {
      const value = (event.target as HTMLInputElement).value;
      this.args.item[field] = value;
    };

  setUserId = (userId: string | null) => {
    this.args.item.user = userId || '';
  };

  validate = (coach: CoachProfile) => {
    const failures: { message: string; attribute: string }[] = [];

    if (!coach.firstName?.trim()) {
      failures.push({
        message: 'First Name is required.',
        attribute: 'firstName',
      });
    }

    if (!coach.lastName?.trim()) {
      failures.push({
        message: 'Last Name is required.',
        attribute: 'lastName',
      });
    }

    if (!coach.user?.trim()) {
      failures.push({
        message: 'User ID is required.',
        attribute: 'user',
      });
    }

    return failures.length > 0
      ? Validation.fromFailures(failures)
      : Validation.success();
  };

  <template>
    <EditRecord
      @editLabel="Edit Coach"
      @item={{@item}}
      @context={{@context}}
      @buttonSize="btn-md"
      @initialState={{initialState @item}}
      @validate={{this.validate}}
      @maxDepth={{1}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow visually-hidden">Coach identity</legend>

          <div class="mb-3">
            <label for="{{uniqueId}}-user" class="form-label required">
              User account
            </label>
            <CoachUserPicker
              @inputId="{{uniqueId}}-user"
              @userId={{@item.user}}
              @setUserId={{this.setUserId}}
            >
              <:suggestion as |suggestion|>
                {{#if suggestion.disabled}}
                  <span
                    class="text-decoration-line-through"
                  >{{suggestion.user.title}}</span>
                  <small>(Existing coach)</small>
                {{else}}
                  {{suggestion.user.title}}
                {{/if}}
              </:suggestion>
              <:after>
                <div class="form-text">
                  Connects the coach to an existing user account. Please create
                  it first if necessary.
                </div>
              </:after>
            </CoachUserPicker>
          </div>

          <div class="row">
            <div class="col-md-2 mb-3">
              <label for="{{uniqueId}}-prefix" class="form-label">Prefix</label>
              <input
                type="text"
                class="form-control"
                id="{{uniqueId}}-prefix"
                value={{@item.prefix}}
                placeholder="Dr."
                {{on "input" (this.setString "prefix")}}
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="{{uniqueId}}-firstName" class="form-label required">
                First Name
              </label>
              <input
                type="text"
                class="form-control"
                id="{{uniqueId}}-firstName"
                value={{@item.firstName}}
                required
                {{on "input" (this.setString "firstName")}}
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="{{uniqueId}}-lastName" class="form-label required">
                Last Name
              </label>
              <input
                type="text"
                class="form-control"
                id="{{uniqueId}}-lastName"
                value={{@item.lastName}}
                required
                {{on "input" (this.setString "lastName")}}
              />
            </div>
            <div class="col-md-2 mb-3">
              <label for="{{uniqueId}}-suffix" class="form-label">Suffix</label>
              <input
                type="text"
                class="form-control"
                id="{{uniqueId}}-suffix"
                value={{@item.suffix}}
                placeholder="PCC"
                {{on "input" (this.setString "suffix")}}
              />
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}

export default CreateCoach;
